import { isEmpty } from 'lodash-es';
import React, { useState } from 'react';

import { ISelectOption, ISelectPreset } from '~/src/dash/components/dropdowns/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  Col,
  Cols,
  IndependentSelectPill,
  spacing,
  Text,
  TextButton,
  TKey,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { ITProps } from '~/src/dash/publicaUI';
import { useBaseStore } from '~/src/dash/views/base/contexts';

export interface IFilterRowObject {
  defaultSelectedOptionIDs?: Set<string | number>; // set of default selected option IDs for the filter to be used in case of reset/clear
  disableSearch?: boolean; // true will disable search for this filter
  disableSort?: boolean; // true will disable sorting for this filter
  displayID?: boolean; // true will display the ID of the filter
  displayLabelWithSelection?: boolean; // only available when selectedLimit is 1 & isSelectionRequired is true
  displaySelectedOption?: boolean; // use only if selectedLimit is 1
  enableActiveFilter?: boolean; // true will enable the active filter functionality
  enableLabelTooltip?: boolean; // true will enable the tooltip for the label
  extractStringToSort?: (option: ISelectOption) => string; // function to extract a string to sort the options
  isCheckboxHidden?: boolean; // true will hide the checkbox in the filter pill
  isSelectionRequired?: boolean; // true will require a selection to be made before applying the filter
  isSuperUserOnly?: boolean; // true will only show the filter to super users
  key: string; // unique key for the filter
  mutuallyExclusiveGroup?: string; // optional group name for mutually exclusive filters
  onLoadDisplayActiveOnly?: boolean; // true will load the filter with only active options
  presets?: ISelectPreset[]; // presets for the filter
  selectedLimit?: number; // number of selected options to display in the filter pill
  selectedOptionIDs?: Set<string | number>; // set of selected option IDs to display in the filter pill
  selectedOptionIDsOnReset?: Set<string | number>; // set of selected option IDs to display if clear all is invoked
  tKey: TKey; // translation key for the filter label
  tProps?: ITProps; // translation properties for the filter label
  hasChildrenIDs?: boolean; // true means the filter has children ids populated ie bidder groups, channel groups, etc
  defaultSelectedChildrenIDs?: Set<string | number>; // set of default selected children option IDs for the filter to be used in case of reset/clear
  hasCustomLogic?: boolean; // true will help flag the filter if it needs custom logic
}

export interface IFilterFetchObject {
  getList: (
    session: ISessionState,
    user: IUserState,
    args?: Record<string, unknown>,
  ) => Promise<ISelectOption[]>;
}

export interface IFilterQueryObject {
  queryKey: string; // key to use in the query params for this filter
  formatQueryValue: (value: Set<string | number>) => string | number | Array<string | number>; // function to format the query value for the filter
}

export interface IExtendedIFilterRowObject
  extends IFilterRowObject,
    IFilterFetchObject,
    IFilterQueryObject {}

interface IProps {
  cypressParentName: string;
  enableClear: boolean; // true will display the clear filters button
  filtersConfig: Record<string, IFilterRowObject>;
  filtersToOptionsMap: Record<string, ISelectOption[]>;
  handleClearFilters: () => void; // clear all filters function
  handleApplySelect: (value: Set<number | string>, key?: string | number) => void;
  selectedFilters: Record<string, Set<string | number>>; // selected filters to display in the filter pills
  loadState: LoadingState; // loading state for the filters
}

export const FilterRowV2 = ({
  cypressParentName,
  filtersToOptionsMap,
  filtersConfig,
  loadState,
  selectedFilters,
  handleClearFilters,
  handleApplySelect,
}: IProps): React.ReactNode => {
  const { isSuperUser } = useBaseStore();
  const { t } = usePublicaTranslation();

  const [resetCounter, setResetCounter] = useState(0);

  const handleClearFilterPills = (): void => {
    setResetCounter((prev) => prev + 1);
    handleClearFilters();
  };

  return (
    <Cols
      max
      largeHorizontalPadding
      style={{ position: 'relative', alignItems: 'center' }}
      className={`${cypressParentName}-filterrowv2`}
    >
      <Col style={{ minWidth: '104px' }}>
        <Cols gap='8px' style={{ alignItems: 'center', minHeight: '42px' }}>
          <Col displayFlex>
            <IconV2 name='filter' small />
          </Col>
          <Col>
            <Text small bold tKey='Filters' style={{ lineHeight: 1 }} />
          </Col>
        </Cols>
      </Col>
      <Col flex>
        <Cols
          wrap
          style={{
            alignItems: 'center',
            height: '100%',
            padding: '5px 8px 5px 0px',
            columnGap: '2px',
            rowGap: spacing.extraSmall,
          }}
          key={resetCounter} // reset the component when resetCounter changes
        >
          {!isEmpty(filtersConfig) &&
            loadState !== LoadingStateLoading &&
            Object.values(filtersConfig).map((filter) => {
              if (filter.isSuperUserOnly && !isSuperUser) {
                return;
              }
              return (
                <Col>
                  <IndependentSelectPill
                    disableSearch={filter.disableSearch || false}
                    disableSort={filter.disableSort || false}
                    displayID={filter.displayID || false}
                    displayLabelWithSelection={filter.displayLabelWithSelection || false}
                    displaySelectedOption={filter.displaySelectedOption || false}
                    enableActiveFilter={filter.enableActiveFilter || false}
                    enableLabelTooltip={filter.enableLabelTooltip || false}
                    extractStringToSort={filter.extractStringToSort}
                    handleApplySelect={handleApplySelect}
                    isCheckboxHidden={filter.isCheckboxHidden || false}
                    isDisabled={false}
                    isSelectionRequired={filter.isSelectionRequired || false}
                    key={filter.key}
                    labelTKey={filter.tKey}
                    onLoadDisplayActiveOnly={filter.onLoadDisplayActiveOnly || false}
                    options={filtersToOptionsMap[filter.key] || []}
                    selectKey={filter.key}
                    selectedLimit={filter.selectedLimit || 0}
                    selectedOptionIDs={selectedFilters[filter.key] || new Set()}
                    presets={filter.presets}
                  />
                </Col>
              );
            })}
          <Col displayFlex smallHorizontalPadding>
            <TextButton size='extraSmall' onClick={handleClearFilterPills}>
              {t('ClearAll')}
            </TextButton>
          </Col>
        </Cols>
      </Col>
    </Cols>
  );
};
